/**
 * Creates a Stripe bank account token for US accounts.
 * Used for Connect account payout setup: frontend tokenizes, sends token id to backend; backend attaches to Connect account.
 * Raw account/routing numbers never leave the browser to our server — only the token does.
 */
import { loadStripe } from "@stripe/stripe-js";

export interface BankAccountDetails {
  accountHolderName: string;
  accountHolderType: "individual" | "company";
  routingNumber: string;
  accountNumber: string;
  country?: string;
  currency?: string;
}

let stripePromise: ReturnType<typeof loadStripe> | null = null;

function getStripe(publishableKey: string) {
  if (!stripePromise) {
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
}

/**
 * Tokenize bank account details with Stripe.js. Returns token id (btok_xxx) to send to backend.
 * Requires REACT_APP_STRIPE_PUBLISHABLE_KEY to be set.
 */
export async function createBankAccountToken(
  publishableKey: string,
  details: BankAccountDetails
): Promise<string> {
  if (!publishableKey) {
    throw new Error("Stripe publishable key is not set. Add REACT_APP_STRIPE_PUBLISHABLE_KEY to .env");
  }
  const stripe = await getStripe(publishableKey);
  if (!stripe) {
    throw new Error("Could not load Stripe");
  }
  const { accountHolderName, accountHolderType, routingNumber, accountNumber, country = "US", currency = "usd" } = details;
  const result = await stripe.createToken("bank_account", {
    country,
    currency,
    account_holder_name: accountHolderName.trim(),
    account_holder_type: accountHolderType,
    routing_number: routingNumber.replace(/\D/g, ""),
    account_number: accountNumber.replace(/\D/g, ""),
  });
  if (result.error) {
    throw new Error(result.error.message || "Failed to verify bank account");
  }
  if (!result.token?.id) {
    throw new Error("No token returned from Stripe");
  }
  return result.token.id;
}
